import { forwardRef, Inject, Injectable } from "@angular/core";
import { DmServiceAbstract } from "../abstraction/dm-service.abstract";
import { IOneProfileSerResAttr } from "../../components/tiles/type-ahead/type-ahead-contracts";
import { DMLoggerService } from "./dmlogger.service";
import { APIConstants, BasicProfileRequestEnum, OneProfileGraphQLFields, Services } from "../application.constants";
import { IMasterSkillsLevels, IOneProfileAPIResponse, IOneProfileBulkAPIResponse, ISkillDetails } from "./contracts/one-profile.contracts";
import { ConfigManagerService } from "./configmanager.service";
import { IBulkResourceProfilesData, IGeoLocationFilter, IMasterGeoLocationData, IResourceBasicProfile, IResourceBasicProfileData, ISearchProfile, ISearchProfileData, ISearchSkillsData, ISkillDetailsHierarchy, ISkillSearchResult, ISubconOnboardingSnapshotData, OneProfileGraphQLResponse } from "./contracts/one-profile-graphql.contracts";
import { UserInfoService } from "@fxp/fxpservices";
import { DataService } from "./data.service";



@Injectable()
export class OneProfileGraphQLService extends DmServiceAbstract {

    private oneProfileGraphQLEndpoint: string;
    private defaultPageSize: number = 1000;
    private takeCitiesLimit: string;

    public constructor(
        @Inject(forwardRef(() => UserInfoService)) private fxpUserInfoService: UserInfoService,
        @Inject(DMLoggerService) dmLogger: DMLoggerService,
        @Inject(ConfigManagerService) private configManagerService: ConfigManagerService,
        @Inject(DataService) private dataService: DataService,
    ) {

        super(dmLogger, Services.OneProfileGraphQLService);
        this.configManagerService.initialize().then(() => {
            this.oneProfileGraphQLEndpoint = this.configManagerService.getValue<string>("oneProfileGraphQLEndpoint");
            this.defaultPageSize = this.configManagerService.getValue<number>("oneProfileGraphQLPageSize") || this.defaultPageSize;
            this.takeCitiesLimit = this.configManagerService.getValue<string>("takeCitiesLimit");
        });
    }

    public fetchUserProfileGraphQL(alias: string): Promise<OneProfileGraphQLResponse<IResourceBasicProfileData>> {
        const graphQLFields: string[] = OneProfileGraphQLFields.ResourceBasicProfile;
        const graphQLQuery = this.getBasicProfileGraphQuery(BasicProfileRequestEnum.ALIAS, alias, graphQLFields);
        return this.sendOPGraphQLRequestAsync(graphQLQuery, APIConstants.FetchUserProfileGraphQL);
    }

    public fetchBulkUserProfileGraphQL(aliasArray: string[]): Promise<OneProfileGraphQLResponse<IBulkResourceProfilesData>> {
        const graphQLFields: string[] = OneProfileGraphQLFields.BulkResourceProfiles;
        const graphQLQuery = this.getBulkResourceProfilesGraphQuery(aliasArray, graphQLFields);
        return this.sendOPGraphQLRequestAsync(graphQLQuery, APIConstants.FetchBulkUserProfileGraphQL);
    }

    public fetchSkillsGraphQLBySearchText(searchText: string, orgType: string): Promise<OneProfileGraphQLResponse<ISearchSkillsData>> {
        const graphQLFields: string[] = OneProfileGraphQLFields.SearchSkills;
        const graphQLQuery = this.getSearchSkillsGraphQLQuery(searchText, orgType, graphQLFields);
        return this.sendOPGraphQLRequestAsync(graphQLQuery, APIConstants.FetchSkillsGraphQLBySearchText);
    }

    public fetchCitiesBasedOnSearchTextGraphQL(searchText: string): Promise<OneProfileGraphQLResponse<IMasterGeoLocationData>> {
        const graphQLFields: string[] = OneProfileGraphQLFields.GetCitiesBySearchText;
        const graphQLQuery = searchText ? this.getGeoLocationGraphQuery(graphQLFields, 1, parseInt(this.takeCitiesLimit, 10), { cityName: true }, "", "", searchText, true) : "";
        return this.sendOPGraphQLRequestAsync(graphQLQuery, APIConstants.FetchCitiesBasedOnSearchTextGraphQL);
    }

    public fetchProfileSearchGraphQL(searchValue: string, resourceCategoryTypes: number[]): Promise<OneProfileGraphQLResponse<ISearchProfileData>> {
        const graphQLFields: string[] = OneProfileGraphQLFields.ProfileSearch;
        const graphQLQuery = this.getSearchProfileGraphQuery(searchValue, graphQLFields, resourceCategoryTypes);
        return this.sendOPGraphQLRequestAsync(graphQLQuery, APIConstants.FetchProfileSearchGraphQL);
    }

    public fetchSubconOnboardingGraphQL(userAlias: string): Promise<OneProfileGraphQLResponse<ISubconOnboardingSnapshotData>> {
        const graphQLFields: string[] = OneProfileGraphQLFields.SubconOnboarding;
        const graphQLQuery = this.getSubconOnboardingGraphQuery(userAlias, graphQLFields);
        return this.sendOPGraphQLRequestAsync(graphQLQuery, APIConstants.FetchSubconOnboardingStatusGraphQL, [403]);
    }

    public async isStatelessCountry(countryCode: string): Promise<boolean> {
        const graphQLFields: string[] = OneProfileGraphQLFields.GetCitiesByCountryCode;
        const graphQLQuery = this.getGeoLocationGraphQuery(graphQLFields, 1, 20, { countryCode: true }, countryCode);
        try {
            const cityDetails: OneProfileGraphQLResponse<IMasterGeoLocationData> =  await this.sendOPGraphQLRequestAsync(graphQLQuery, APIConstants.FetchCityBasedOnCountryCodeGraphQL);
            if (cityDetails && cityDetails.data && cityDetails.data.masterGeoLocation && cityDetails.data.masterGeoLocation.length > 0 && cityDetails.data.masterGeoLocation[0].countryCode
                && cityDetails.data.masterGeoLocation[0].states && cityDetails.data.masterGeoLocation[0].states.length > 0 
                && !cityDetails.data.masterGeoLocation[0].states[0].stateCode && !cityDetails.data.masterGeoLocation[0].states[0].stateName
                && cityDetails.data.masterGeoLocation[0].states[0].cities && cityDetails.data.masterGeoLocation[0].states[0].cities.length > 0
                && cityDetails.data.masterGeoLocation[0].states[0].cities[0].cityName
            ) {
                return true;
            } else {
                return false;
            }
        } catch {
            return false;
        }     
    }

    public mapResourceBasicProfileToProfile(source: IResourceBasicProfile): IOneProfileAPIResponse {
        const res: IOneProfileAPIResponse = {
            Alias: source && source.contact ? source.contact.alias : null,
            DisplayName: source && source.contact ? source.contact.displayName : null,
            EmailName: source && source.contact ? source.contact.emailName : null,
            BusinessPartnerId: source && source.organization ? source.organization.businessPartnerId : null,
            SDODomain: source && source.organization ? source.organization.sdoDomain : null,
            ReportsToDisplayName: source && source.manager ? source.manager.managerDisplayName : null,
            CountryCode: source && source.geography ? source.geography.countryCode : null
        };

        return res;
    }

    public mapBulkResourceBasicProfile(source: IResourceBasicProfile): IOneProfileBulkAPIResponse {
        const res: IOneProfileBulkAPIResponse = {
            DisplayName: source && source.contact ? source.contact.displayName : null,
            CompanyCode: source && source.organization ? source.organization.companyCode : null,
            CompanyName: source && source.organization ? source.organization.companyName : null,
            Alias: source && source.contact ? source.contact.alias : null,
            PersonnelNumber: source && source.organization ? source.organization.personnelNumber : null
        };

        return res;
    }

    public mapSearchSkills(source: ISkillSearchResult): ISkillDetails {
        const res: ISkillDetails = {
            SkillName: source ? source.skillName : null,
            IsLeafLevel: source ? (source.isLeafLevel ? 1 : 0) : 0,
            JobSkillId: source ? source.jobSkillId : null,
            DimensionStr: source && source.dimension ? source.dimension.map((d) => ({
                DomainId: d.domainId,
                DomainName: d.domainName
            })) : null,
        };

        return res;
    }

    public mapSkillDetailsHierarchy(source: ISkillDetailsHierarchy): IMasterSkillsLevels {
        const res: IMasterSkillsLevels = {
            JobSkillId: source ? source.skillId : null,
            LevelValue: source ? source.skillName : null,
            JobSkillDepth: source ? source.skillDepth : null,
            SkillStatus: source ? source.skillStatus : null,
            DimensionStr: source && source.dimensionStr ? source.dimensionStr.map((d) => ({
                DomainId: d.domainId,
                DomainName: d.domainName
            })) : null,

            MasterSkillLevels: []
        };

        if (source.masterSkillLevels && source.masterSkillLevels.length > 0) {
            source.masterSkillLevels.forEach((level2: ISkillDetailsHierarchy) => {
                res.MasterSkillLevels.push(this.mapSkillDetailsHierarchy(level2));
            });
        }

        return res;
    }

    public mapSearchProfile(source: ISearchProfile): IOneProfileSerResAttr {
        const res: IOneProfileSerResAttr = {
            Alias: source && source.contact ? source.contact.alias : null,
            BusinessPartnerId: source && source.organization ? source.organization.businessPartnerId : null,
            AddressBookTitleDesc: source && source.employment ? source.employment.addressBookTitleDesc : null,
            DisplayName: source && source.contact ? source.contact.displayName : null,
            EmailName: source && source.contact ? source.contact.emailName : null,
            FirstName: source && source.contact ? source.contact.displayName : null,
            FullName: source && source.contact ? source.contact.displayName : null,
            PreferredFirstName: source && source.contact ? source.contact.displayName : null,
            Domain: source && source.organization ? source.organization.domain : null,
            LastName: "",
            WorkLocation: source && source.geography ? {
                City: source.geography.workLocation ? source.geography.workLocation.city : null,
                State: source.geography.workLocation ? source.geography.workLocation.state : null,
                CountryCode: source.geography.workLocation ? source.geography.workLocation.countryCode : null,
                CountryName: source.geography.workLocation ? source.geography.workLocation.countryName : null
            } : null
        };

        return res;
    }

    public getBasicProfileGraphQuery(profileIdentifierType: string, identifierValue: string, fields: string[]): string {
        if (fields && fields.length > 0 && identifierValue && profileIdentifierType) {
            const fieldsString = fields.join(" ");
            return `query {
                    resourceBasicProfile(profileIdentifierType: ${profileIdentifierType}, identifier: "${identifierValue}") {
                        ${fieldsString}
                    }
                }`;
        }

        return "";
    }

    public getBulkResourceProfilesGraphQuery(aliases: string[], fields: string[]): string {
        if (aliases && fields && fields.length > 0) {
            const aliasesString = aliases.map((alias) => (alias !== null && alias !== undefined) ? `"${alias}"` : "null").join(", ");
            const fieldsString = fields.join(" ");
            return `query {
                bulkResourceProfiles(aliases: [${aliasesString}]) {
                    ${fieldsString}
                }
            }`;
        }

        return "";
    }

    public getSearchSkillsGraphQLQuery(searchText: string, orgType: string, fields: string[]): string {
        if (fields && fields.length > 0) {
            const fieldsString = fields.join(" ");
            const searchTextString = (searchText !== null && searchText !== undefined) ? `"${searchText}"` : "null";
            return `query {
                searchSkills(skillSearchRequest: {count: 20, gracePeriod: 1, statuses: [NEW, ACTIVE, RETIRING], searchText: ${searchTextString}, orgType: ${orgType}}) {
                    searchResults {${fieldsString}}
                }
            }`;
        }

        return "";
    }

    public getSkillsHierarchyGraphQuery(categoryId: string, gracePeriod: number, orgType: string, fields: string[], pageNumber: number = 1, pageSize: number = this.defaultPageSize): string {
        if (fields && fields.length > 0) {
            const fieldsString = fields.join(" ");
            const gracePeriodString = gracePeriod ? `, gracePeriod: ${gracePeriod}` : "";
            const orgTypeString = orgType ? `"${orgType}"` : orgType;
            return `query {
                masterSkill(pageNumber: ${pageNumber}, pageSize: ${pageSize}, tenantName: ${orgTypeString} ${gracePeriodString}, statusFilter: "New,Active,Retiring") {
                    skillHierarchy(
                            categoryId: ${categoryId}
                    ) {
                        ${fieldsString}
                    }
                }
            }`;
        }

        return "";
    }

    public getGeoLocationGraphQuery(
        fields: string[],
        pageNumber: number = 1,
        pageSize: number = this.defaultPageSize,
        geoLocationFilter: IGeoLocationFilter = null,
        countryCode?: string,
        stateName?: string,
        cityName?: string,
        enablePartialSearch?: boolean
    ): string {
        const fieldsString = fields.join(" ");

        // Construct the filter string
        let filterString = "{}";

        if (geoLocationFilter !== null && (geoLocationFilter.countryCode || geoLocationFilter.stateName || geoLocationFilter.cityName)) {
            const filter: { [key: string]: string } = {};
            if (geoLocationFilter.countryCode) {
                if (countryCode === null || countryCode === undefined) {
                    return "";
                }
                filter["countryCode"] = `"${countryCode}"`;
            }
            if (geoLocationFilter.stateName) {
                // if countryCode is valid but stateName is null or empty we pass null to fetch all cities of that country
                // some countries like Singapore has no states
                filter["stateName"] = (stateName !== null && stateName !== undefined && stateName.trim() !== "") ? `"${stateName}"` : "null";
            }
            if (geoLocationFilter.cityName) {
                filter["cityName"] = `"${cityName}"`;
            }
            if (enablePartialSearch) {
                filter["enablePartialMatchForCity"] = `${enablePartialSearch}`;
            }
            filterString = `{ ${Object.entries(filter).map(([key, value]) => `${key}: ${value}`).join(", ")} }`;
        }

        return `query {
            masterGeoLocation(request: { filter: ${filterString}, pageNumber: ${pageNumber}, pageSize: ${pageSize} }) {
                ${fieldsString}
            }
        }`;

    }

    public getSearchProfileGraphQuery(searchText: string, fields: string[], resourceCategoryTypes: number[]): string {
        if (fields && fields.length > 0) {
            const fieldsString = fields.join(" ");

            // Construct the filter string
            let filterString = "{}";
            const filter: { [key: string]: string } = {};

            filter["searchText"] = `"${searchText}"`;

            if (resourceCategoryTypes && resourceCategoryTypes.length > 0) {
                filter["resourceCategoryTypes"] = `[${resourceCategoryTypes.join(",")}]`;
            }
            filterString = `{ ${Object.entries(filter).map(([key, value]) => `${key}: ${value}`).join(", ")} }`;
            return `query {
                searchProfiles(filter: ${filterString}) {
                    ${fieldsString}
                }
            }`;
        }

        return "";
    }

    public getSearchCompanyCodesGraphQuery(fields: string[], searchText: string, pageNumber: number = 1, pageSize: number = this.defaultPageSize): string {
        if (fields && fields.length > 0) {
            const fieldsString = fields.join(" ");
            const searchTextString = searchText ? `"${searchText}"` : searchText;
            return `query {
                masterEntity(pageNumber: ${pageNumber}, pageSize: ${pageSize}) {
                    companyCodes(searchText: ${searchTextString}) {
                        ${fieldsString}
        }}}`;
        }
        return "";
    }

    public getCustomerEngagementDomainsGraphQuery(fields: string[], pageNumber: number = 1, tenantName: string): string {
        if (fields && fields.length) {
            const fieldsString = fields.join(" ");

            let filterString = "{}";
            const filter: { [key: string]: string } = {};

            if (tenantName) {
                filter["tenantName"] = tenantName ? `"${tenantName}"` : tenantName;
                filterString = `${Object.entries(filter).map(([key, value]) => `${key}: ${value}`).join(", ")}`;
            }
            return `query {
                masterEntity(pageNumber: ${pageNumber}, pageSize: ${this.defaultPageSize}) {
                    servicesDeliveryOrganizations(${filterString}) {
                        ${fieldsString}
                    }}}`;
        }
        return "";
    }

    public getSubconOnboardingGraphQuery(userAlias: string, fields: string[]): string {
        if (userAlias && fields && fields.length > 0) {
            const fieldsString = fields.join(" ");
            return `query {
                subconOnboardingSnapshotForPjm(pjmAlias: "${userAlias}") {
                    ${fieldsString}
                }}`;
        }
        return "";
    }

    public async fetchAllPages<T, R>(
        getQuery: (pageNumber: number) => string, // Function to generate the GraphQL query for each page
        apiName: string,
        processResponse: (response: OneProfileGraphQLResponse<T>, results: R[]) => Promise<boolean>, // Callback to process the response and determine if more pages should be fetched
    ): Promise<R[]> {
        let hasMoreData = true;
        let pageNumber = 1;
        const results: R[] = [];

        while (hasMoreData) {
            const graphQLQuery = getQuery(pageNumber);

            const response: OneProfileGraphQLResponse<T> = await this.sendOPGraphQLRequestAsync(graphQLQuery, apiName);

            // Use the callback to process the response and determine if more pages should be fetched
            hasMoreData = await processResponse(response, results);

            pageNumber++; // Increment the page number for the next request
        }

        return results;
    }

    private sendOPGraphQLRequestAsync(query: string, apiName: string, suppressErrorCode?: number[]): Promise<any> {

        const headers = {
            "Content-Type": "application/json",
            "X-IsUICall": "true",
            "X-IsSelfCall": this.fxpUserInfoService.isActingOnBehalfOf() ? "false" : "true",
            "X-CurrentUserEntraId": this.fxpUserInfoService.getCurrentUserOID()
        };

        // prepare the request body
        const requestBody = { query };

        return this.dataService.postData(this.oneProfileGraphQLEndpoint, "", apiName, requestBody, false, headers, suppressErrorCode, true);
    }
}